import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  CircularProgress,
  Alert
} from '@mui/material';
import { CalendarToday, TrendingUp } from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { format } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
  ReferenceLine
} from 'recharts';

// Colors used consistently across dashboard
const GREEN_COLOR = '#10b981'; // Positive values (enrollments > deletions)
const RED_COLOR = '#ef4444';   // Negative values (deletions > enrollments)

const AnalyticsGraphCard = ({
  analyticsData,
  dateRange,
  setDateRange,
  fetchAnalytics,
  loading,
  error,
  maxDate = new Date()
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const handleOpenCalendar = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleCloseCalendar = () => {
    setOpen(false);
  };

  const handleDateRangeChange = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleApplyDateRange = () => {
    fetchAnalytics();
    handleCloseCalendar();
  };

  // Fill missing days with count = 0
  const fillMissingDays = (data, startDate, endDate) => {
    const filledData = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    const dateMap = new Map(data.map(item => [item.date, { count: item.count, enrollments: item.enrollments, deletions: item.deletions }]));

    while (current <= end) {
      const dateStr = format(current, 'yyyy-MM-dd');
      const dayData = dateMap.get(dateStr) || { count: 0, enrollments: 0, deletions: 0 };
      filledData.push({
        date: dateStr,
        count: dayData.count,
        enrollments: dayData.enrollments || 0,
        deletions: dayData.deletions || 0
      });
      current.setDate(current.getDate() + 1);
    }

    return filledData;
  };

  const chartData =
    analyticsData?.daily_counts && analyticsData.date_range
      ? fillMissingDays(
        analyticsData.daily_counts,
        analyticsData.date_range.start_date,
        analyticsData.date_range.end_date
      ).map((item, index) => ({
        ...item,
        index: index + 1,
        displayDate: item.date
      }))
      : [];

  // Calculate a symmetric Y-axis domain so that 0 (origin) is always fixed at the center
  const minCount = chartData.length > 0 ? Math.min(...chartData.map(d => d.count)) : 0;
  const maxCount = chartData.length > 0 ? Math.max(...chartData.map(d => d.count)) : 0;
  const absMax = Math.max(Math.abs(minCount), Math.abs(maxCount));
  const paddedMax = Math.ceil(absMax + absMax * 0.1) + 1; // Add 10% padding
  const yAxisDomain = [-paddedMax, paddedMax]; // Symmetric: same range above and below 0

  return (
    <Card
      sx={{
        mb: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(64, 81, 138, 0.12)',
        borderRadius: 3,
        boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.75, sm: 2.25, md: 2.5 } }}>
        {/* Header and Date Range Selector */}
        <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.5 }, minWidth: 0 }}>
            <Box sx={{ p: { xs: 1.2, sm: 1.5 }, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 2, display: 'flex', alignItems: 'center' }}>
              <TrendingUp color='primary' />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.25 }}>
              Employee Enrollment
            </Typography>
          </Box>

          {/* Date Range Selector */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid rgba(64, 81, 138, 0.18)',
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: 'rgba(64, 81, 138, 0.04)',
                width: { xs: '100%', sm: 'auto' },
                cursor: 'pointer'
              }}
              onClick={handleOpenCalendar}
            >
              <CalendarToday sx={{color: '#473e8a',  fontSize: 18}} />
              <Typography variant="body2" sx={{ color: '#333', fontSize: { xs: '0.82rem', sm: '0.88rem' }, flex: 1 }}>
                {format(dateRange[0].startDate, 'MMM dd')} -{' '}
                {format(dateRange[0].endDate, 'MMM dd')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Date Range Picker Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseCalendar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box sx={{ p: 0, width: 'fit-content' }}>
            <DateRangePicker
              ranges={dateRange}
              onChange={handleDateRangeChange}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={['#40518A']}
              staticRanges={[]}
              inputRanges={[]}
              maxDate={maxDate}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button onClick={handleCloseCalendar} variant="outlined" size="small">
                Cancel
              </Button>
              <Button onClick={handleApplyDateRange} variant="contained" size="small">
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Analytics Data */}
        {!loading && !error && analyticsData && (
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'rgba(64, 81, 138, 0.05)', border: '1px solid rgba(64, 81, 138, 0.12)', borderRadius: 1.5, px: 1.25, py: 0.8 }}>
                Net Registrations: <strong>{analyticsData.total_registrations}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'rgba(64, 81, 138, 0.05)', border: '1px solid rgba(64, 81, 138, 0.12)', borderRadius: 1.5, px: 1.25, py: 0.8 }}>
                Date Range: {analyticsData.date_range?.start_date} to{' '}
                {analyticsData.date_range?.end_date}
              </Typography>
            </Box>

            {/* Bar Chart */}
            {chartData.length > 0 ? (
              <Box sx={{ flexGrow: 1, minHeight: { xs: 300, sm: 360 } }}>
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 16, right: 10, left: -8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                    <ReferenceLine y={0} stroke="#ccc" strokeWidth={1} />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fontSize: 11, fill: '#667085' }}
                      interval="preserveStartEnd"
                      minTickGap={24}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: '#666' }}
                      axisLine={{ stroke: '#ccc' }}
                      tickCount={9}
                      domain={yAxisDomain}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(64, 81, 138, 0.08)' }}
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const item = payload[0].payload;
                        return (
                          <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px',
                            fontSize: '13px'
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Date: {item.displayDate}</div>
                            <div>Net Change: {item.count}</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>Enrollments: {item.enrollments || 0}</div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>Deletions: {item.deletions || 0}</div>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="count"
                      name="Net Change"
                      radius={[4, 4, 0, 0]}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.count >= 0 ? GREEN_COLOR : RED_COLOR}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 400,
                  border: '1px dashed #ccc',
                  borderRadius: 1,
                  bgcolor: '#f5f5f5'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No registration data available for the selected date range
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsGraphCard;
